// src/components/TrainingCalendarPage.js
import axios from "../utils/axiosConfig";
import React, { useState, useEffect, useMemo } from "react";
import "../Style/Calendar.css";
import {
  buildGoogleCalendarUrl,
  buildOutlookCalendarUrl,
  buildYahooCalendarUrl,
  downloadICS,
  eventHasValidDates,
} from "../utils/calendarUtils";

const weekdayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Fallback trainings if the API call fails
const FALLBACK_TRAININGS = [];

function buildCalendarMatrix(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const weeks = [];
  let current = 1 - firstDay;
  for (let w = 0; w < 6; w++) {
    const week = [];
    for (let d = 0; d < 7; d++, current++) {
      week.push(current < 1 || current > daysInMonth ? null : current);
    }
    weeks.push(week);
  }
  return weeks;
}

function toCalendarPayload(t) {
  return {
    id: t.id,
    title: t.title,
    description: t.description || "",
    location: t.location || "",
    start: t.date,
    end: t.endDate || t.date,
    startTime: t.time || undefined,
    endTime: t.endTime || undefined,
  };
}

export default function TrainingCalendarPage() {
  const today = new Date();
  const [viewDate, setViewDate] = useState(
    new Date(today.getFullYear(), today.getMonth(), 1)
  );
  const [trainings, setTrainings] = useState(FALLBACK_TRAININGS);
  const [selected, setSelected] = useState(null);

  const yearNum = viewDate.getFullYear();
  const monthNum = viewDate.getMonth();
  const weeks = useMemo(
    () => buildCalendarMatrix(yearNum, monthNum),
    [yearNum, monthNum]
  );
  const displayMonthYear = viewDate.toLocaleString("default", {
    month: "long",
    year: "numeric",
  });

  useEffect(() => {
    const fetchTrainings = async () => {
      try {
        const token = localStorage.getItem("token");
        const resp = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/trainings/`,
          { headers: { Authorization: `Token ${token}` } }
        );
        if (Array.isArray(resp.data) && resp.data.length > 0) {
          setTrainings(resp.data);
        }
      } catch (err) {
        console.error("Failed to fetch trainings:", err);
      }
    };
    fetchTrainings();
  }, []);

  const trainingsByDay = (day) => {
    const key = `${yearNum}-${String(monthNum + 1).padStart(2, "0")}-${String(
      day
    ).padStart(2, "0")}`;
    return trainings.filter((t) => t.date === key);
  };

  const handlePrev = () =>
    setViewDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const handleNext = () =>
    setViewDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  const goToToday = () =>
    setViewDate(new Date(today.getFullYear(), today.getMonth(), 1));

  const upcoming = useMemo(() => {
    const todayKey = today.toISOString().slice(0, 10);
    return trainings
      .filter((t) => t.date >= todayKey)
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(0, 5);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trainings]);

  return (
    <div className="calendar-page">
      <header className="calendar-header">
        <span className="calendar-eyebrow">Training</span>
        <h2>Training Sessions Calendar</h2>
        <p className="calendar-lede">
          Browse upcoming training sessions and add them to your personal
          calendar in one click.
        </p>
      </header>

      <div className="calendar-toolbar">
        <div className="calendar-nav">
          <button onClick={handlePrev} aria-label="Previous month">‹</button>
          <span>{displayMonthYear}</span>
          <button onClick={handleNext} aria-label="Next month">›</button>
        </div>
        <button className="calendar-today-btn" onClick={goToToday}>
          Today
        </button>
      </div>

      <div className="calendar-layout">
        <div className="calendar-main">
          <table className="calendar-grid">
            <thead>
              <tr>
                {weekdayNames.map((d, i) => (
                  <th key={i}>{d}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeks.map((week, wIdx) => (
                <tr key={wIdx}>
                  {week.map((day, dIdx) => {
                    const isToday =
                      day === today.getDate() &&
                      monthNum === today.getMonth() &&
                      yearNum === today.getFullYear();
                    const items = day ? trainingsByDay(day) : [];
                    return (
                      <td
                        key={dIdx}
                        className={`${isToday ? "today" : ""} ${
                          day && items.length ? "has-events" : ""
                        } ${!day ? "empty" : ""}`}
                      >
                        {day && <div className="day-number">{day}</div>}
                        {items.map((t) => (
                          <button
                            key={t.id}
                            className="calendar-item training"
                            onClick={() => setSelected(t)}
                          >
                            {t.time && (
                              <span className="calendar-item-time">
                                {t.time}
                              </span>
                            )}
                            <span className="calendar-item-title">{t.title}</span>
                          </button>
                        ))}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <aside className="calendar-side">
          <h3 className="calendar-side-title">Upcoming sessions</h3>
          {upcoming.length === 0 ? (
            <p className="calendar-side-empty">
              No upcoming trainings currently.
            </p>
          ) : (
            upcoming.map((t) => (
              <button
                key={t.id}
                className="calendar-side-card"
                onClick={() => setSelected(t)}
              >
                <div className="calendar-side-date">
                  {formatPretty(t.date)}
                  {t.time ? ` · ${t.time}` : ""}
                </div>
                <div className="calendar-side-title-row">{t.title}</div>
                {t.location && (
                  <div className="calendar-side-loc">{t.location}</div>
                )}
              </button>
            ))
          )}
        </aside>
      </div>

      {selected && (
        <TrainingDetailsModal
          training={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

function formatPretty(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function TrainingDetailsModal({ training, onClose }) {
  const payload = toCalendarPayload(training);
  const valid = eventHasValidDates(payload);
  const googleUrl = valid && buildGoogleCalendarUrl(payload);
  const outlookUrl = valid && buildOutlookCalendarUrl(payload);
  const yahooUrl = valid && buildYahooCalendarUrl(payload);

  return (
    <div className="calendar-modal-backdrop" onClick={onClose}>
      <div
        className="calendar-modal"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="calendar-modal-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
        <h3>{training.title}</h3>
        <p className="calendar-modal-meta">
          {formatPretty(training.date)}
          {training.time ? ` · ${training.time}` : ""}
          {training.endTime ? ` – ${training.endTime}` : ""}
        </p>
        {training.location && (
          <p className="calendar-modal-loc">📍 {training.location}</p>
        )}
        {training.description && (
          <p className="calendar-modal-desc">{training.description}</p>
        )}

        {valid && (
          <div className="calendar-modal-actions">
            <span className="calendar-buttons-title">Add to calendar</span>
            <div className="calendar-buttons-row">
              <a
                href={googleUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="calendar-btn google"
              >
                Google
              </a>
              <a
                href={outlookUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="calendar-btn outlook"
              >
                Outlook
              </a>
              <a
                href={yahooUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="calendar-btn yahoo"
              >
                Yahoo
              </a>
              <button
                onClick={() => downloadICS(payload)}
                className="calendar-btn ics"
              >
                Apple / .ics
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
